import { STRIPE_URL } from '@/constants/constants';
import { SubscriptionItemData } from '@/types/stripe.types';
import toast from 'react-hot-toast';
import { apiFetch } from '../apiWrapper';

type SubscriptionDto = {
  customerId: string;
  priceId: string;
  paymentMethodId: string;
  couponId: string;
};

type StripeOnBoardingDto = {
  companyId: string | undefined;
  companyName: string;
  name: string;
  phone: string;
  email: string;
  country: string | undefined;
  price: string;
  withTrial: boolean;
  couponId?: string;
  paymentMethodId?: string;
};
export const stripeCreateSubscription = async (customerData: SubscriptionDto) => {
  try {
    const response = await fetch(`${STRIPE_URL}/create-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during creating Stripe Subscription');
  }
};

export const stripeOnBoarding = async (customerData: StripeOnBoardingDto) => {
  try {
    const response = await fetch(`${STRIPE_URL}/onboarding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    });

    if (response.ok) {
      const data = await response.json();
      if (!data) {
        window.location.href = '/subscription-exists';
      } else {
        return data;
      }
    }
  } catch (error) {
    throw new Error('Something went wrong for onboarding');
  }
};

export const createStripeCustomer = async (customerData: any) => {
  try {
    const response = await fetch(`${STRIPE_URL}/create-customer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    throw new Error('Something went wrong for creating Stripe Customer');
  }
};

export const createStripeAccountForCustomer = async (dataForAccount: any) => {
  try {
    const response = await fetch(
      `${STRIPE_URL}/create-business-account/${dataForAccount.companyId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataForAccount),
      },
    );
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.message,
        stripeCode: errorData.stripeCode,
      };
    }
  } catch (err) {
    throw new Error('Something went wrong during creating Stripe Account');
  }
};

export const retrieveStripeSubscription = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/retrieve-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Subscription');
  }
};

export const retrieveStripeCustomer = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/retrieve-customer/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Customer');
  }
};

export const retrieveStripePaymentMethodList = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/retrieve-payment-methods`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Payment Method');
  }
};

export const stripeChangeDefaultMethod = async (customer: string, defaultPaymentMethod: string) => {
  const data = { customer, defaultPaymentMethod };
  console.log(data);
  try {
    const response = await fetch(`${STRIPE_URL}/update-default-payment-method`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (response.ok) {
      return response.ok;
    }
  } catch (err) {
    throw new Error('Something went wrong during changing default payment method');
  }
};

export const retrieveStripeInvoicesList = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/retrieve-invoices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Invoices');
  }
};

export const deletePaymentMethod = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/delete-payment-method`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during deleting payment method');
  }
};

export const updateBillingInfo = async (data: any) => {
  try {
    const response = await apiFetch(`${STRIPE_URL}/update-billing-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (response?.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during updating Stripe Subscription');
  }
};

export const retrieveStripeTaxIdsList = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/retrieve-tax-ids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Tax Ids');
  }
};

export const retrieveStripeLocations = async (id: string, stripeAccountId: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/locations/${id}?accountId=${stripeAccountId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Terminals');
  }
};

type StripeAddressType = {
  line1: string;
  city: string;
  postal_code: string;
  state: string;
  country: string;
};

export type Location = {
  display_name: string;
  address: StripeAddressType;
  metadata?: Record<string, unknown>;
};
export const createStripeLocation = async (location: Location) => {
  try {
    const response = await fetch(`${STRIPE_URL}/create-location`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(location),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during creating Stripe Location');
  }
};

export type Terminal = {
  label: string;
  registration_code: string;
  location: string;
  metadata?: Record<string, unknown>;
};

export const createStripeTerminal = async (terminal: Terminal) => {
  try {
    const response = await fetch(`${STRIPE_URL}/create-reader`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(terminal),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during creating Stripe Terminal');
  }
};

export const retrieveProductData = async (priceId: string, productId: string) => {
  try {
    const res = await fetch(`${STRIPE_URL}/product-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priceId, productId }),
    });
    const data = await res.json();
    return data;
  } catch (error) {
    toast.error('Something went wrong in gettting subscription data');
  }
};

export const retrieveStripeAccount = async (accountId: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/retrieve-account/${accountId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Account');
  }
};

export const getPriceById = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/get-price/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Price');
  }
};

export const getCouponById = async (id: string) => {
  try {
    const response = await fetch(`${STRIPE_URL}/get-coupon/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during retrieving Stripe Coupon');
  }
};

export const createItemAtSubscription = async (id: string, item: SubscriptionItemData) => {
  try {
    const response = await fetch(`${STRIPE_URL}/create-item-at-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, item }),
    });
    if (response.ok) {
      const data = response.json();
      return data;
    }
  } catch (err) {
    throw new Error('Something went wrong during creating item at subscription');
  }
};
export const stripeAddItemsToSubscription = async (
  subData: SubscriptionItemData,
  withTrial: boolean = false,
) => {
  try {
    const response = await fetch(`${STRIPE_URL}/add-items-to-subscription?withTrial=${withTrial}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subData),
    });
    if (response?.ok) {
      const data = response.json();
      return data;
    } else {
      throw new Error('Error adding items to subscription');
    }
  } catch (err) {
    throw new Error('Something went wrong during adding items to subscription');
  }
};
